import { Button } from '@components/common/button';
import { DictType } from '@pma/types';
import axios from 'axios';
import { parseCookies } from 'nookies';
import { useMutation } from '@tanstack/react-query';
import { toast } from '@hooks/use-toast';
import { ACCESS_TOKEN, queryClient } from '@pma/utils/constants';
import { SERVER_URL, PIN_TASK_ENDPOINT } from '@pma/utils/api-routes';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@components/common/tooltip';
import useTranslator from '@hooks/use-translator';
import { HTMLAttributes } from 'react';
import { useAuth } from '@pma/stores/auth-store';
import { Pin, PinOff } from 'lucide-react';
import { cn } from '@pma/utils/helpers';

interface Props extends HTMLAttributes<HTMLDivElement> {
  task: DictType;
}

const PinTask = ({ task, children }: Props) => {
  const { translate } = useTranslator();
  const accessToken = parseCookies()[ACCESS_TOKEN];
  const navigate = useNavigate();
  const { authConfig } = useAuth();
  const [searchParam] = useSearchParams();

  const newParams = new URLSearchParams(searchParam);

  const userId = authConfig?.id;

  const pinnedByUser = task?.pinned_by_user_id?.includes(userId);

  const pinTask = async () => {
    const response = await axios.post(
      SERVER_URL + PIN_TASK_ENDPOINT + `/${task?.id}`,
      {
        task_id: task?.id,
      },
      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      },
    );

    return response.data;
  };

  const pinTaskMutation = useMutation({
    mutationKey: ['pin-task', task?.id],
    mutationFn: pinTask,
    onSuccess: (data) => {
      newParams.set('revalidate', 'tasks');
      navigate(`?${newParams.toString()}`);
      queryClient.invalidateQueries({queryKey:['grouped-tasks']})
      toast({
        title: translate('Success'),
        description: `${task?.title} ${translate(pinnedByUser ? 'has been unpinned' : 'has been pinned')}`,
        variant: 'success',
      });
    },
    onError: (error) => {
      toast({
        title: translate('Error'),
        description: `${translate('Unable to pin')} ${task?.title}`,
        variant: 'error',
      });
    },
  });

  return (
    <Tooltip>
      <TooltipTrigger
        onClick={() => {
          pinTaskMutation.mutate();
        }}
      >
        {children ? (
          children
        ) : (
          <Button
            variant={'ghost'}
            size={'icon'}
            className='flex items-center cursor-pointer rounded-full justify-between w-6 h-6  text-xs m-0 p-1'
          >
            {pinnedByUser ? <PinOff size={15} /> : <Pin size={15} />}
          </Button>
        )}
      </TooltipTrigger>
      <TooltipContent
        className={cn(
          'flex items-center gap-1',
          pinnedByUser ? 'bg-destructive' : 'bg-primary',
        )}
      >
        <h4>{translate(pinnedByUser ? 'Unpin' : 'Pin')}</h4>
      </TooltipContent>
    </Tooltip>
  );
};

export default PinTask;
